import { environment } from '../../environments/environment';
import { Alert, AlertConditionType } from '../models/alert';
import { PROJECT_QUERY_PARAM, buildProjectLink } from './project-deep-link';

/**
 * One-click links from an alert to the project that fired it.
 *
 * CANONICAL SHAPE — this is the contract DataServices mirrors for out-of-app
 * notifications (email, Slack, Telegram):
 *
 *     {base}/{tab}?project={urlEncodedProjectName}&alertId={alertId}
 *     e.g. https://hybrid-dev.vericure.io/temperature?project=Unnamed_2026-08-22_04-22-12&alertId=abc123
 *
 * Deliberate choices, since DS has to reproduce this exactly:
 *
 *   PROJECT NAME, not an id. It is what `?project=` already resolves against,
 *   and the resolution is company-scoped — a name from another company opens the
 *   picker rather than the project. Switching to an opaque id would mean a second
 *   lookup path with different security properties.
 *   URL-ENCODED. Real names carry spaces, '&', '#' and '+', any of which
 *   silently truncates an unencoded query.
 *   alertId TRAILS the project. It is additive: a link without it still works,
 *   which matters because the project param is the part that must not break.
 *   TAB CHOSEN BY CONDITION. Landing on a view that cannot show the thing that
 *   fired is a worse first impression than landing somewhere generic.
 */

/** Query parameter carrying the alert id. */
export const ALERT_QUERY_PARAM = 'alertId';

/** Where each condition is best examined. */
export const ALERT_TAB_BY_CONDITION: Record<AlertConditionType, string> = {
  // Temperature limits are read off the temperature trace and its overheat band.
  TemperatureThreshold: '/temperature',
  // A dropped sensor shows as a fiber fault on the health view.
  SensorDisconnected: '/health',
  // Offline periods are drawn as the hatch overlay, clearest against loss.
  DeviceOffline: '/health',
  // Grouped with connectivity, matching DataServices. A system error is not
  // drawn on either view, so this was a coin-flip; agreeing on one answer is
  // worth more than either choice, since a mismatch means two builders emit
  // different URLs for the same alert.
  SystemError: '/health',
};

/** Fallback for a condition this build does not know about. */
export const DEFAULT_ALERT_TAB = '/temperature';

/**
 * Route for an alert's condition.
 *
 * Unknown conditions fall back rather than failing: DS may add a condition type
 * before the client learns about it, and a link to the default view is far
 * better than a dead one.
 */
export function tabForCondition(
  conditionType: string | null | undefined
): string {
  const known = ALERT_TAB_BY_CONDITION[conditionType as AlertConditionType];
  return known ?? DEFAULT_ALERT_TAB;
}

/** In-app route parts for `router.navigate`. */
export interface AlertRoute {
  path: string;
  queryParams: Record<string, string>;
}

/**
 * The in-app navigation for an alert, or null when it names no project.
 *
 * Null rather than a link to nowhere: an alert without a project cannot be
 * one-clicked anywhere useful, and the UI should not offer the affordance.
 */
export function alertRoute(alert: Alert | null | undefined): AlertRoute | null {
  const project = alert?.projectName?.trim();
  if (!project) {
    return null;
  }
  const queryParams: Record<string, string> = { [PROJECT_QUERY_PARAM]: project };
  if (alert?.id) {
    queryParams[ALERT_QUERY_PARAM] = alert.id;
  }
  return { path: tabForCondition(alert?.conditionType), queryParams };
}

/**
 * Base URL for ABSOLUTE links — the form DS emits into notifications.
 *
 * Config-driven so hybrid-dev and prod differ by environment rather than by
 * code. Falls back to the running origin, which keeps in-app "copy this link"
 * correct even where the config has not been set.
 */
export function appBaseUrl(): string {
  const configured = (environment as { appBaseUrl?: string }).appBaseUrl;
  if (typeof configured === 'string' && configured.trim().length > 0) {
    return configured.replace(/\/+$/, '');
  }
  try {
    return globalThis.location?.origin ?? '';
  } catch {
    return '';
  }
}

/**
 * The absolute canonical link for an alert — the exact string DS should emit.
 *
 * Shares buildProjectLink with the in-app copy-link affordance, so a link
 * pasted from the UI and a link sent by email are byte-identical.
 */
export function alertDeepLink(
  alert: Alert | null | undefined,
  baseUrl: string = appBaseUrl()
): string | null {
  const route = alertRoute(alert);
  if (!route) {
    return null;
  }
  const withProject = buildProjectLink(
    baseUrl,
    route.path,
    route.queryParams[PROJECT_QUERY_PARAM]
  );
  const alertId = route.queryParams[ALERT_QUERY_PARAM];
  return alertId
    ? `${withProject}&${ALERT_QUERY_PARAM}=${encodeURIComponent(alertId)}`
    : withProject;
}
